'use client';

import { c as _c } from "react/compiler-runtime";
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/next';
import { usePathname } from 'next/navigation';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import { Suspense, useEffect, useState } from 'react';
import { Toaster } from 'sonner';
import { DesktopAppIntegrations } from './desktop-app-integrations';
import { GlobalKeyboardShortcutsProvider } from './global-keyboard-shortcuts';
import { GlobalRealtimeSubscriptions } from './global-realtime-subscriptions';
import SuspendedPostHogPageView from './posthog-page-view';
import { PostHogProvider } from './posthog-provider';
import { PrNavigationProvider } from './pr-navigation';
import { TitleManager } from './title-manager';
import { CommandMenu } from '@/commands/command-dialog';
import { CommandProvider } from '@/commands/command-provider';
import { AuthProtectionWrapper } from '@/components/auth/auth-protection-wrapper';
import { AuthenticatedFeatures } from '@/components/auth/authenticated-features';
import { DesktopOpenPrompt } from '@/components/desktop-handoff/desktop-open-prompt';
import { HelpWidget } from '@/components/help/help-widget';
import { ImpersonationBanner } from '@/components/impersonation/impersonation-banner';
import { InstallPromptHost } from '@/components/install-prompts/install-prompt-host';
import { CodeNewSessionShortcut } from '@/components/keyboard-shortcuts/code-new-session-shortcut';
import { KeyboardShortcutsController } from '@/components/keyboard-shortcuts/keyboard-shortcuts-controller';
import { AtlasProvider } from '@/components/providers/atlas-provider';
import { ProfilePreferencesSynchronizer } from '@/components/providers/profile-preferences-synchronizer';
import QueryProvider from '@/components/providers/query-provider';
import { ThemeProvider } from '@/components/providers/theme-provider';
import { ToDesktopAuthProvider } from '@/components/providers/todesktop-auth-provider';
import { SamlSsoSignInOutcomeReporter } from '@/components/saml-sso-sign-in-outcome-reporter';
import { useIdleCallback } from '@/hooks/use-idle-callback';
import { scrubEventUrl } from '@/lib/analytics/sensitive-url-params';
import { isDesktopTrayPath } from '@/lib/desktop-tray-window';
import { isPrV2Route as isPrV2Path } from '@/lib/pr-v2/route-utils';
import { isSuperAdminPath, SUPER_ADMIN_THEME } from '@/lib/super-admin-theme';
import { AuthProvider } from '@/providers/auth-provider';
import { SubscriptionStatusProvider } from '@/providers/subscription-status-provider';
function AtlasVisibilityController() {
  const $ = _c(5);
  const pathname = usePathname();
  const t0 = pathname ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = isPrV2Path(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isPrV2Route = t1;
  let t2;
  let t3;
  if ($[2] !== isPrV2Route) {
    t2 = () => {
      document.documentElement.toggleAttribute("data-hide-atlas", isPrV2Route);
      if (isPrV2Route) {
        window.Atlas?.chat?.closeWindow();
      }
      return _temp;
    };
    t3 = [isPrV2Route];
    $[2] = isPrV2Route;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  return null;
}

// Defers AtlasProvider on PR routes so initial diff content renders first, then enables
// non-critical provider work on idle time or first user interaction.
function _temp() {
  document.documentElement.removeAttribute("data-hide-atlas");
}
function DeferredPrRouteAtlasProvider() {
  const $ = _c(12);
  const pathname = usePathname();
  const isPrV2Route = isPrV2Path(pathname ?? "");
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname?.startsWith("/pr/") ?? false;
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isPrRoute = t0;
  let t1;
  if ($[2] !== isPrRoute) {
    t1 = () => !isPrRoute;
    $[2] = isPrRoute;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [hasActivatedDeferredProviders, setHasActivatedDeferredProviders] = useState(t1);
  const shouldRenderDeferredProviders = !isPrRoute || hasActivatedDeferredProviders;
  let t2;
  let t3;
  if ($[4] !== isPrRoute) {
    t2 = () => {
      if (isPrRoute) {
        setHasActivatedDeferredProviders(true);
      }
    };
    t3 = [isPrRoute];
    $[4] = isPrRoute;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useIdleCallback(t2, t3);
  let t4;
  let t5;
  if ($[7] !== isPrRoute || $[8] !== shouldRenderDeferredProviders) {
    t4 = () => {
      if (!isPrRoute || shouldRenderDeferredProviders) {
        return;
      }
      const enableDeferredProviders = () => {
        setHasActivatedDeferredProviders(true);
      };
      window.addEventListener("pointerdown", enableDeferredProviders, {
        once: true,
        passive: true
      });
      window.addEventListener("keydown", enableDeferredProviders, {
        once: true
      });
      return () => {
        window.removeEventListener("pointerdown", enableDeferredProviders);
        window.removeEventListener("keydown", enableDeferredProviders);
      };
    };
    t5 = [isPrRoute, shouldRenderDeferredProviders];
    $[7] = isPrRoute;
    $[8] = shouldRenderDeferredProviders;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  if (isPrV2Route || !shouldRenderDeferredProviders) {
    return null;
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AtlasProvider />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

/** Providers every route needs, including the desktop tray popover window. */
function SharedProviders(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const t1 = isSuperAdminPath(pathname) ? SUPER_ADMIN_THEME : undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SpeedInsights beforeSend={scrubEventUrl} />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== children) {
    t3 = <PostHogProvider><QueryProvider><AuthProvider><ToDesktopAuthProvider><NuqsAdapter>{children}</NuqsAdapter></ToDesktopAuthProvider></AuthProvider></QueryProvider></PostHogProvider>;
    $[1] = children;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t3) {
    t4 = <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={true} disableTransitionOnChange={true} forcedTheme={t1}>{t2}{t3}</ThemeProvider>;
    $[3] = t1;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export function Providers(t0) {
  const $ = _c(20);
  const {
    children
  } = t0;
  const pathname = usePathname();
  if (isDesktopTrayPath(pathname)) {
    let t1;
    if ($[0] !== children) {
      t1 = <SharedProviders>{children}</SharedProviders>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <GlobalRealtimeSubscriptions />;
    t2 = <DesktopAppIntegrations />;
    t3 = <ImpersonationBanner />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  const t4 = isSuperAdminPath(pathname) ? SUPER_ADMIN_THEME : "dark";
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      style: {
        pointerEvents: "auto"
      }
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4) {
    t6 = <Toaster position="bottom-right" theme={t4} toastOptions={t5} />;
    $[6] = t4;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SuspendedPostHogPageView />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t10;
  let t11;
  let t12;
  let t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Suspense fallback={null}><TitleManager /></Suspense>;
    t9 = <Analytics beforeSend={scrubEventUrl} />;
    t10 = <AtlasVisibilityController />;
    t11 = <HelpWidget />;
    t12 = <DesktopOpenPrompt />;
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t8;
    $[13] = t9;
  } else {
    t10 = $[9];
    t11 = $[10];
    t12 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <AuthenticatedFeatures><ProfilePreferencesSynchronizer /><InstallPromptHost /><SamlSsoSignInOutcomeReporter /><DeferredPrRouteAtlasProvider /><CodeNewSessionShortcut /><KeyboardShortcutsController /><CommandMenu /></AuthenticatedFeatures>;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== children) {
    t14 = <CommandProvider>{t13}<AuthProtectionWrapper><SubscriptionStatusProvider>{children}</SubscriptionStatusProvider></AuthProtectionWrapper></CommandProvider>;
    $[15] = children;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t14 || $[18] !== t6) {
    t15 = <SharedProviders><PrNavigationProvider><GlobalKeyboardShortcutsProvider>{t1}{t2}{t3}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t14}</GlobalKeyboardShortcutsProvider></PrNavigationProvider></SharedProviders>;
    $[17] = t14;
    $[18] = t6;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  return t15;
}